import { useAnalysis } from "@/hooks/useAnalysis";
import { useDomains } from "@/hooks/useDomains";
import { useSonnerToast } from "@/hooks/useSonnerToast";
import { useUrlValidation } from "@/hooks/useUrlValidation";
import type { Idea, Page } from "@/schemas";
import { useStore } from "@/store";
import { useCallback, useMemo, useRef, useState } from "react";

export interface UseSearchbarReturn {
  // State
  searchQuery: string;
  showResults: boolean;
  isDisabled: boolean;
  filteredResults: { pages: Page[]; ideas: Idea[] };
  showAddNewDialog: boolean;
  pendingSearchQuery: string;

  // Actions
  setSearchQuery: (query: string) => void;
  setShowResults: (show: boolean) => void;
  setShowAddNewDialog: (show: boolean) => void;
  handleSelectPage: (domainId: string, pageId: string) => void;
  handleSelectIdea: (domainId: string, ideaId: string) => void;
  handleCreateContext: () => void;
  handleAddManualPage: () => Promise<void>;
  clearSearch: () => void;

  // Refs
  searchRef: React.RefObject<HTMLDivElement | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * Custom hook for searchbar state and logic.
 * Search is scoped to `currentDomain` — domain switching lives in WorkspaceSwitcher.
 */
export const useSearchbar = (): UseSearchbarReturn => {
  /* ===== Dependencies ===== */
  const { currentDomain } = useDomains();
  const { ensureUrlContext } = useAnalysis();
  const { validateUrl } = useUrlValidation();
  const { toast } = useSonnerToast();
  const setFocusedItem = useStore((s) => s.setFocusedItem);
  const pages = useStore((s) => s.pages);
  const ideas = useStore((s) => s.ideas);

  /* ===== Local State ===== */
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [showAddNewDialog, setShowAddNewDialog] = useState(false);
  const [pendingSearchQuery, setPendingSearchQuery] = useState("");

  /* ===== Refs ===== */
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* ===== Computed Values ===== */
  const isDisabled = !currentDomain;

  const domainPages = useMemo(
    () =>
      currentDomain
        ? pages.filter((p) => p.domainId === currentDomain.id)
        : [],
    [pages, currentDomain],
  );

  const domainIdeas = useMemo(
    () =>
      currentDomain
        ? ideas.filter((idea) => idea.domain_id === currentDomain.id)
        : [],
    [ideas, currentDomain],
  );

  const filteredResults = useMemo(() => {
    if (searchQuery.trim() === "") {
      return { pages: domainPages, ideas: domainIdeas };
    }

    const q = searchQuery.toLowerCase();

    const filteredPages = domainPages.filter((p) => {
      if (!p.url) {
        // Draft page — match by title or input_keyword
        const label = (p.title || p.input_keyword || "").toLowerCase();
        return label.includes(q);
      }
      return p.url.toLowerCase().includes(q);
    });

    const filteredIdeas = domainIdeas.filter((idea) => {
      const title = (idea.title || "").toLowerCase();
      const keyword = (idea.target_keyword || "").toLowerCase();
      return title.includes(q) || keyword.includes(q);
    });

    return { pages: filteredPages, ideas: filteredIdeas };
  }, [searchQuery, domainPages, domainIdeas]);

  /* ===== Actions ===== */
  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setShowResults(false);
  }, []);

  const handleSelectPage = useCallback(
    (_domainId: string, pageId: string) => {
      setFocusedItem({ id: pageId, type: "page" });
      clearSearch();
    },
    [setFocusedItem, clearSearch],
  );

  const handleSelectIdea = useCallback(
    (_domainId: string, ideaId: string) => {
      setFocusedItem({ id: ideaId, type: "idea" });
      clearSearch();
    },
    [setFocusedItem, clearSearch],
  );

  const handleCreateContext = useCallback(() => {
    const { isValid, validUrl, errorMessage } = validateUrl(searchQuery);
    if (!isValid) {
      toast({
        variant: "destructive",
        title: "Invalid URL",
        description: errorMessage,
      });
      return;
    }
    setPendingSearchQuery(validUrl!);
    setShowAddNewDialog(true);
  }, [searchQuery, validateUrl, toast]);

  const handleAddManualPage = useCallback(async () => {
    if (!pendingSearchQuery) return;
    await ensureUrlContext(pendingSearchQuery);
    setShowAddNewDialog(false);
    clearSearch();
  }, [pendingSearchQuery, ensureUrlContext, clearSearch]);

  return {
    // State
    searchQuery,
    showResults,
    isDisabled,
    filteredResults,
    showAddNewDialog,
    pendingSearchQuery,

    // Actions
    setSearchQuery,
    setShowResults,
    setShowAddNewDialog,
    handleSelectPage,
    handleSelectIdea,
    handleCreateContext,
    handleAddManualPage,
    clearSearch,

    // Refs
    searchRef,
    inputRef,
  };
};
